import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Stack,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import {
  DragIndicator as DragIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import MediaSelector from '../media/MediaSelector';
import CKRichEditor from './CKRichEditor';
import type { AnyContentBlock } from '../../models';


interface ContentBlocksEditorProps {
  blocks: AnyContentBlock[];
  onChange: (blocks: AnyContentBlock[]) => void;
  disabled?: boolean;
}

const SortableBlock = ({ block, index, onEdit, onDelete, disabled }: {
  block: AnyContentBlock;
  index: number;
  onEdit: (index: number, block: AnyContentBlock) => void;
  onDelete: (index: number) => void;
  disabled?: boolean;
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: index, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const renderBlock = () => {
    switch (block.type) {
      case 'paragraph':
        return (
          <TextField
            fullWidth
            multiline
            rows={4}
            value={block.content}
            onChange={(e) => onEdit(index, { ...block, content: e.target.value })}
            placeholder="Enter text content..."
            disabled={disabled}
          />
        );
      case 'image':
        return (
          <MediaSelector
            value={block.content}
            onChange={(value) => onEdit(index, { ...block, content: value })}
            fileTypeFilter="images"
            previewSize="medium"
            disabled={disabled}
          />
        );
      case 'heading':
        return (
          <Stack direction="row" spacing={2} alignItems="center">
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel>Header Level</InputLabel>
              <Select
                value={block.level || 1}
                label="Header Level"
                onChange={(e) => onEdit(index, { ...block, level: Number(e.target.value) })}
                disabled={disabled}
              >
                <MenuItem value={1}>H1</MenuItem>
                <MenuItem value={2}>H2</MenuItem>
                <MenuItem value={3}>H3</MenuItem>
                <MenuItem value={4}>H4</MenuItem>
                <MenuItem value={5}>H5</MenuItem>
                <MenuItem value={6}>H6</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              value={block.content}
              onChange={(e) => onEdit(index, { ...block, content: e.target.value })}
              placeholder="Enter header text..."
              disabled={disabled}
            />
          </Stack>
        );
      case 'html':
        return (
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
            <CKRichEditor
              value={block.content || ''}
              onChange={(value) => onEdit(index, { ...block, content: value })}
              disabled={disabled}
            />
          </Box>
        );
      default:
        return null;
    }
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{ mb: 2 }}
    >
      <CardContent>
        <Grid container spacing={2} alignItems="center">
          <Grid item>
            <Box {...attributes} {...listeners} sx={{ cursor: disabled ? 'default' : 'grab' }}>
              <DragIcon color="action" />
            </Box>
          </Grid>
          <Grid item xs sx={{ width: '100%', minWidth: 0 }}>
            {renderBlock()}
          </Grid>
          <Grid item>
            <IconButton
              color="error"
              onClick={() => onDelete(index)}
              disabled={disabled}
            >
              <DeleteIcon />
            </IconButton>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

const ContentBlocksEditor: React.FC<ContentBlocksEditorProps> = ({ blocks, onChange, disabled = false }) => {
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    if (disabled) return;
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = blocks.findIndex((_, index) => index === active.id);
      const newIndex = blocks.findIndex((_, index) => index === over.id);
      const updatedBlocks = arrayMove(blocks, oldIndex, newIndex);
      onChange(updatedBlocks);
    }
  };

  const addBlock = (type: AnyContentBlock['type']) => {
    if (disabled) return;
    const newBlock: AnyContentBlock = {
      id: Date.now().toString(),
      type,
      content: '',
      ...(type === 'heading' && { level: 1 }),
    };
    onChange([...blocks, newBlock]);
  };

  const removeBlock = (index: number) => {
    if (disabled) return;
    const newBlocks = [...blocks];
    newBlocks.splice(index, 1);
    onChange(newBlocks);
  };

  const updateBlock = (index: number, updates: Partial<AnyContentBlock>) => {
    if (disabled) return;
    const newBlocks = [...blocks];
    newBlocks[index] = { ...newBlocks[index], ...updates };
    onChange(newBlocks);
  };

  return (
    <Box>
      {!disabled && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addBlock('paragraph')}
          >
            Add Text
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addBlock('image')}
          >
            Add Image
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addBlock('heading')}
          >
            Add Header
          </Button>
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => addBlock('html')}
          >
            Add HTML
          </Button>
        </Stack>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={blocks.map((_, index) => index)}
          strategy={verticalListSortingStrategy}
        >
          {blocks.map((block, index) => (
            <SortableBlock
              key={block.id}
              block={block}
              index={index}
              onEdit={updateBlock}
              onDelete={removeBlock}
              disabled={disabled}
            />
          ))}
        </SortableContext>
      </DndContext>
    </Box>
  );
};

export default ContentBlocksEditor;
